import {
	H3, Spacer, SuccessChip, Text, WarnChip
} from '@components';
import { CHIP_SIZE, SPACER_DIRECTION, TCPIIncrease, TEXT_WEIGHT } from '@types';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import styles from './CPIIncreasePanel.module.scss';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import { formatDate as dateFormat } from '@utils';
import { CPIIncreaseScheduleButton } from './CPIIncreaseScheduleButton.tsx';
import { CPIIncreaseEditButton } from './CPIIncreaseEditButton.tsx';
import { CPIIncreaseDeleteButton } from './CPIIncreaseDeleteButton.tsx';

type TProps = {
	cpiIncrease?: TCPIIncrease;
	isDisabled?: boolean;
}

const CPIIncreaseNone = ({ isDisabled }: { isDisabled: boolean; }) => (
	<>
		<div>
			<Text weight={TEXT_WEIGHT.SEMI_BOLD}>No CPI increases</Text>
			<Spacer size={4} />
			<Text>You can schedule an increase to be applied automatically to the base rates.</Text>
		</div>
		<CPIIncreaseScheduleButton isDisabled={isDisabled} />
	</>
);

const CPIIncreaseScheduled = ({ cpiIncrease, isDisabled }: { cpiIncrease: TCPIIncrease; isDisabled: boolean; }) => (
	<div className={styles.row}>
		<Text>{dateFormat(new Date(cpiIncrease.scheduled_at), 'DATE_WITH_SHORT_MONTH_NAME')}</Text>
		<Text>{cpiIncrease.cpi_percentage}%</Text>
		<div className={styles.rowActions}>
			<WarnChip
				text={'Scheduled'}
				CustomIcon={PendingOutlinedIcon}
				size={CHIP_SIZE.MEDIUM}
			/>
			<Spacer size={12} dir={SPACER_DIRECTION.VERTICAL}/>
			<CPIIncreaseEditButton isDisabled={isDisabled} />
			<CPIIncreaseDeleteButton isDisabled={isDisabled} />
		</div>
	</div>
);

const CPIIncreaseProcessed = ({ cpiIncrease, isDisabled = false }: { cpiIncrease: TCPIIncrease; isDisabled: boolean; }) => (
	<>
		<div className={styles.row}>
			<Text>{dateFormat(new Date(cpiIncrease.scheduled_at), 'DATE_WITH_SHORT_MONTH_NAME')}</Text>
			<Text>{cpiIncrease.cpi_percentage}%</Text>
			<SuccessChip
				text={'Completed'}
				CustomIcon={CheckCircleOutlineOutlinedIcon}
				size={CHIP_SIZE.MEDIUM}
			/>
		</div>
		<div className={styles.row}>
			<CPIIncreaseScheduleButton isDisabled={isDisabled} />
		</div>
	</>
);

export const CPIIncreasePanel = ({ cpiIncrease, isDisabled = false }: TProps) => {
	const state = !cpiIncrease ? 'none' : cpiIncrease.processed_at === null ? 'scheduled' : 'processed';
	return (
		<>
			<div className={styles.cpiTile}>
				<H3>CPI Increase</H3>
				{state === 'none' && <CPIIncreaseNone isDisabled={isDisabled} />}
				{state === 'scheduled' && <CPIIncreaseScheduled cpiIncrease={cpiIncrease!} isDisabled={isDisabled}/>}
				{state === 'processed' && <CPIIncreaseProcessed cpiIncrease={cpiIncrease!} isDisabled={isDisabled}/>}
			</div>
		</>
	);
};
